import { BASE_URL } from './constants';
import { getUserSessionCookie } from '../utils/SessionCookie';
import { apiFetch } from './ApiFetch';

export enum CampaignProspectType {
  ACCOUNT = 'account',
  OPPORTUNITY = 'opportunity'
}

export interface Campaign {
  id: string;
  name: string;
  description: string | null;
  create_ts: string;
  creator_id: string;
  company_id: string;
  creator_name?: string;
  prospect_type?: CampaignProspectType;
  archived?: boolean;
}

export interface CampaignsResponse {
  campaigns: Campaign[];
  total_count: number;
}

export interface CreateCampaignRequest {
  name: string;
  description: string;
  prospect_type: CampaignProspectType;
}

export interface CampaignUpdate {
  name?: string;
  description?: string;
  prospect_type?: CampaignProspectType;
}

export async function getCampaigns(
  offset: number = 0,
  limit: number = 100,
  archived?: boolean
): Promise<CampaignsResponse> {
  const params = new URLSearchParams({
    offset: String(offset),
    limit: String(limit)
  });
  if (archived !== undefined) {
    params.set('archived', String(archived));
  }
  const url = `${BASE_URL}/v1/campaigns?${params.toString()}`;
  const userSessionToken = getUserSessionCookie();

  try {
    const response = await apiFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userSessionToken}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    return {
      campaigns: data?.campaigns ?? [],
      total_count: data?.total ?? 0
    };
  } catch (error) {
    console.error('Error fetching campaigns:', error);
    throw error;
  }
}

export async function createCampaign(
  campaignData: CreateCampaignRequest
): Promise<Campaign> {
  const url = `${BASE_URL}/v1/campaigns`;
  const userSessionToken = getUserSessionCookie();

  try {
    const response = await apiFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userSessionToken}`
      },
      body: JSON.stringify(campaignData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating campaign:', error);
    throw error;
  }
}

export async function getCampaign(campaignId: string): Promise<Campaign> {
  const url = `${BASE_URL}/v1/campaigns/${campaignId}`;
  const userSessionToken = getUserSessionCookie();

  try {
    const response = await apiFetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userSessionToken}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching campaign:', error);
    throw error;
  }
}

export async function archiveCampaign(
  campaignId: string,
  archived: boolean
): Promise<Campaign> {
  const url = `${BASE_URL}/v1/campaigns/${campaignId}/archive`;
  const userSessionToken = getUserSessionCookie();

  try {
    const response = await apiFetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userSessionToken}`
      },
      body: JSON.stringify({ archived })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error archiving campaign:', error);
    throw error;
  }
}

export async function duplicateCampaign(campaignId: string): Promise<Campaign> {
  const url = `${BASE_URL}/v1/campaigns/${campaignId}/duplicate`;
  const userSessionToken = getUserSessionCookie();

  try {
    const response = await apiFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userSessionToken}`
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error duplicating campaign:', error);
    throw error;
  }
}

export async function updateCampaign(
  campaignId: string,
  campaignData: CampaignUpdate
): Promise<Campaign> {
  const url = `${BASE_URL}/v1/campaigns/${campaignId}`;
  const userSessionToken = getUserSessionCookie();

  try {
    const response = await apiFetch(url, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${userSessionToken}`
      },
      body: JSON.stringify(campaignData)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error updating campaign:', error);
    throw error;
  }
}
